import { getVaultDirectory, type VaultDirectoryInDb } from '@/api';

/**
 * The vault's listings (a folder, the guest data room) come as
 * one `items` sequence — folders, contracts and plain files together, sorted
 * and paged on the server, at most 100 per page.
 */

/** The largest page the listing endpoints accept. */
export const LISTING_PAGE_LIMIT = 100;

interface ListingPage<TItem> {
  items: TItem[];
  items_count: number;
}

/**
 * Every item of a listing, page after page. The first response comes back with
 * all items, so callers keep the listing's own fields (a folder's uid, name,
 * parent…). For callers that need the whole folder, like a folder picker; the
 * vault page itself shows one page at a time.
 */
export async function fetchAllListingPages<TPage extends ListingPage<unknown>>(
  fetchPage: (limit: number, offset: number) => Promise<TPage>
): Promise<TPage> {
  const first = await fetchPage(LISTING_PAGE_LIMIT, 0);
  const items = [...first.items];
  while (items.length < first.items_count) {
    const next = await fetchPage(LISTING_PAGE_LIMIT, items.length);
    // A listing that shrank while it was read would otherwise loop forever.
    if (next.items.length === 0) break;
    items.push(...next.items);
  }
  return { ...first, items };
}

/** A whole vault folder (the main vault's root when `directoryUid` is omitted). */
export function fetchWholeDirectory(directoryUid?: string): Promise<VaultDirectoryInDb> {
  return fetchAllListingPages(async (limit, offset) => {
    const response = await getVaultDirectory({
      query: { ...(directoryUid ? { directory_uid: directoryUid } : {}), limit, offset },
    });
    if (!response.data) throw new Error('Failed to load the folder.');
    return response.data;
  });
}

type ListedItem = VaultDirectoryInDb['items'][number];

/** A listing's items of one kind, typed as that kind. */
export function itemsOfKind<TItem extends { kind?: string }, TKind extends string>(
  items: TItem[],
  kind: TKind
): Extract<TItem, { kind: TKind }>[] {
  return items.filter((item): item is Extract<TItem, { kind: TKind }> => item.kind === kind);
}

export type ListedDirectory = Extract<ListedItem, { kind: 'directory' }>;
export type ListedContract = Extract<ListedItem, { kind: 'contract' }>;
export type ListedFile = Extract<ListedItem, { kind: 'file' }>;

const TYPE_LABELS: Record<string, string> = {
  folder: 'Folder',
  archive: 'Archive',
  file: 'File',
};

/**
 * What the Type column shows for an item's `type_label`: Folder, Archive or
 * File, or a contract's type as it comes (NDA, MSA…). Sorting by type orders
 * by the raw label, so this only capitalises.
 */
export function vaultTypeLabel(typeLabel: string | undefined): string {
  if (!typeLabel) return '';
  return TYPE_LABELS[typeLabel] ?? typeLabel;
}
